/**
 * useDoSearch — REQ-090 state machine for the admin Delivery Order Search &
 * Detail module (ODE-P07-T03).
 *
 * Flow: `pending` (filters not yet submitted) → `submit()` → `loading` → the
 * guarded search outcome:
 *   200 → `ready` (server page; an empty `rows` array is the REQ-090 empty
 *         state rendered by the module, never a fabricated row)
 *   400 → `error`   (invalid filter — the server rejected it, retryable with
 *         corrected input)
 *   401 → `unauthorized`
 *   403 → `blocked` (server-enforced; the client mirror is UX only)
 *   503/network → `error`
 *
 * Detail (`openDetail(doId)`) is a separate read-only projection with its own
 * state (`idle`/`loading`/`ready`/`not_found`/`unauthorized`/`blocked`/`error`).
 * Nothing here computes completion: the five REQ-074 states and the REQ-073
 * authority flag come from the server projection verbatim.
 *
 * `loadMore()` appends the next keyset page (the cursor is opaque; the client
 * never builds one).
 *
 * Traces to: ODE-PRD-REQ-044, ODE-PRD-REQ-054, ODE-PRD-REQ-073,
 *            ODE-PRD-REQ-074, ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type AdminDoDetail,
  type AdminDoDetailResult,
  type AdminDoSearchPage,
  type AdminDoSearchQuery,
  type AdminDoSearchResult,
} from '../../api/client.js';

/** The raw filter form (all values are strings; empties are omitted). */
export interface DoSearchForm {
  readonly doNumber: string;
  readonly status: string;
  readonly from: string;
  readonly to: string;
  readonly driverRef: string;
  readonly vehicleRef: string;
  readonly limit: string;
}

export const EMPTY_DO_SEARCH_FORM: DoSearchForm = {
  doNumber: '',
  status: '',
  from: '',
  to: '',
  driverRef: '',
  vehicleRef: '',
  limit: '',
};

export type DoSearchListState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly page: AdminDoSearchPage; readonly loadingMore: boolean }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type DoDetailState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly doId: string }
  | { readonly kind: 'ready'; readonly detail: AdminDoDetail }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface UseDoSearchOptions {
  readonly token: string | null;
  /** Test seam: search fetch (default: the shared api client). */
  readonly search?: ((query: AdminDoSearchQuery, token: string) => Promise<AdminDoSearchResult>) | undefined;
  /** Test seam: detail fetch (default: the shared api client). */
  readonly detail?: ((doId: string, token: string) => Promise<AdminDoDetailResult>) | undefined;
}

export interface UseDoSearchResult {
  readonly form: DoSearchForm;
  readonly setForm: (patch: Partial<DoSearchForm>) => void;
  readonly submit: () => void;
  readonly reset: () => void;
  readonly loadMore: () => void;
  readonly state: DoSearchListState;
  readonly detailState: DoDetailState;
  readonly openDetail: (doId: string) => void;
  readonly closeDetail: () => void;
}

/** Maps the raw form to the wire query (empties omitted, limit as a number). */
export function toSearchQuery(form: DoSearchForm): AdminDoSearchQuery {
  const query: Record<string, string | number> = {};
  if (form.doNumber.trim() !== '') query.doNumber = form.doNumber.trim();
  if (form.status.trim() !== '') query.status = form.status.trim();
  if (form.from.trim() !== '') query.from = form.from.trim();
  if (form.to.trim() !== '') query.to = form.to.trim();
  if (form.driverRef.trim() !== '') query.driverRef = form.driverRef.trim();
  if (form.vehicleRef.trim() !== '') query.vehicleRef = form.vehicleRef.trim();
  const limit = Number.parseInt(form.limit, 10);
  if (form.limit.trim() !== '' && Number.isInteger(limit)) query.limit = limit;
  return query;
}

export function useDoSearch(options: UseDoSearchOptions): UseDoSearchResult {
  const { token } = options;
  const [form, setFormState] = useState<DoSearchForm>(EMPTY_DO_SEARCH_FORM);
  const [state, setState] = useState<DoSearchListState>({ kind: 'pending' });
  const [detailState, setDetailState] = useState<DoDetailState>({ kind: 'idle' });
  const [request, setRequest] = useState<{ readonly attempt: number; readonly query: AdminDoSearchQuery } | null>(
    null,
  );
  const attemptRef = useRef(0);
  const stateRef = useRef<DoSearchListState>(state);
  stateRef.current = state;

  const searchRef = useRef(
    options.search ?? ((query: AdminDoSearchQuery, tokenValue: string) => api.searchDeliveryOrders(query, tokenValue)),
  );
  searchRef.current =
    options.search ?? ((query: AdminDoSearchQuery, tokenValue: string) => api.searchDeliveryOrders(query, tokenValue));
  const detailRef = useRef(
    options.detail ?? ((doId: string, tokenValue: string) => api.fetchDeliveryOrderDetail(doId, tokenValue)),
  );
  detailRef.current =
    options.detail ?? ((doId: string, tokenValue: string) => api.fetchDeliveryOrderDetail(doId, tokenValue));

  const setForm = useCallback((patch: Partial<DoSearchForm>) => {
    setFormState((current) => ({ ...current, ...patch }));
  }, []);

  const submit = useCallback(() => {
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, query: toSearchQuery(form) });
  }, [form]);

  const reset = useCallback(() => {
    setFormState(EMPTY_DO_SEARCH_FORM);
    setRequest(null);
    setState({ kind: 'pending' });
    setDetailState({ kind: 'idle' });
  }, []);

  // Initial page load (a fresh submit replaces the list; loadMore appends).
  useEffect(() => {
    if (request === null) return undefined;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    searchRef
      .current(request.query, token)
      .then((result) => {
        if (cancelled) return;
        setState(mapSearchResult(result));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: error instanceof Error ? error.message : 'Network error' });
      });
    return () => {
      cancelled = true;
    };
  }, [token, request]);

  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (current.kind !== 'ready' || !current.page.hasMore || current.page.nextCursor === null) {
      return;
    }
    const cursor = current.page.nextCursor;
    const baseQuery = request?.query ?? {};
    setState({ ...current, loadingMore: true });
    void (async () => {
      if (token === null) return;
      try {
        const result = await searchRef.current({ ...baseQuery, cursor }, token);
        if (result.status === 200 && result.payload !== null) {
          setState((latest) => {
            if (latest.kind !== 'ready') return latest;
            return {
              kind: 'ready',
              loadingMore: false,
              page: {
                ...result.payload!,
                rows: [...latest.page.rows, ...result.payload!.rows],
              },
            };
          });
        } else {
          setState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
        }
      } catch (error: unknown) {
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Network error',
        });
      }
    })();
  }, [request, token]);

  const openDetail = useCallback(
    (doId: string) => {
      if (token === null) {
        setDetailState({ kind: 'unauthorized' });
        return;
      }
      setDetailState({ kind: 'loading', doId });
      void detailRef
        .current(doId, token)
        .then((result) => {
          if (result.status === 200 && result.payload !== null) {
            setDetailState({ kind: 'ready', detail: result.payload });
          } else if (result.status === 404) {
            setDetailState({ kind: 'not_found' });
          } else if (result.status === 401) {
            setDetailState({ kind: 'unauthorized' });
          } else if (result.status === 403) {
            setDetailState({ kind: 'blocked', message: result.message ?? 'Forbidden' });
          } else {
            setDetailState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
          }
        })
        .catch((error: unknown) => {
          setDetailState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Network error',
          });
        });
    },
    [token],
  );

  const closeDetail = useCallback(() => {
    setDetailState({ kind: 'idle' });
  }, []);

  return { form, setForm, submit, reset, loadMore, state, detailState, openDetail, closeDetail };
}

function mapSearchResult(result: AdminDoSearchResult): DoSearchListState {
  switch (result.status) {
    case 200:
      return result.payload === null
        ? { kind: 'error', message: 'Respons pencarian tidak valid.' }
        : { kind: 'ready', page: result.payload, loadingMore: false };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Forbidden' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}
